import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@common/auth/use-auth';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@common/http/query-client';

export interface UserProfile {
  id: number;
  name: string;
  avatar: string;
  is_kids: boolean;
  maturity_rating?: string;
}

interface ActiveProfileContextType {
  activeProfile: UserProfile | null;
  selectProfile: (profile: UserProfile) => void;
  logoutProfile: () => void;
  profiles: UserProfile[];
  isLoading: boolean;
  isSwitchingProfile: boolean; // NEW: For loading overlay during switch
}

const ActiveProfileContext = createContext<ActiveProfileContextType>(null!);

export function ActiveProfileProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Track if we're currently switching profiles (for loading overlay)
  const [isSwitchingProfile, setIsSwitchingProfile] = useState(false);

  // Initialize from Storage
  const [activeProfile, setActiveProfile] = useState<UserProfile | null>(() => {
    try {
      if (typeof window !== 'undefined') {
        const stored = localStorage.getItem('bootk_active_profile');
        return stored ? JSON.parse(stored) : null;
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  });

  // Fetch Profiles for managing switching
  const { data: profiles = [], isLoading } = useQuery({
    queryKey: ['user', 'profiles'],
    queryFn: () => apiClient.get('user/profiles').then(r => r.data),
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  });

  // CRITICAL: Clear profile when user logs out or session expires
  useEffect(() => {
    if (!user) {
      localStorage.removeItem('bootk_active_profile');
      localStorage.removeItem('bootk_active_profile_id');
      setActiveProfile(null);
    }
  }, [user]);

  /**
   * Switch to a new profile with SOFT RESET (no page reload).
   * 
   * Flow:
   * 1. Show loading overlay (isSwitchingProfile = true)
   * 2. Update localStorage with new profile
   * 3. Clear ALL cached data from React Query
   * 4. Navigate to Home (triggers fresh data fetch)
   * 5. Hide loading overlay
   */
  const selectProfile = async (profile: UserProfile) => {
    if (!profile) {
      setActiveProfile(null);
      return;
    }

    // 1. Show loading overlay
    setIsSwitchingProfile(true);

    try {
      // 2. Update localStorage with new profile
      localStorage.setItem('bootk_active_profile', JSON.stringify(profile));
      localStorage.setItem('bootk_active_profile_id', profile.id.toString());

      // 3. Update state
      setActiveProfile(profile);

      // 4. CLEAR ALL CACHED DATA - removes old profile's data completely
      // This is the key to avoiding stale data flash
      queryClient.removeQueries();

      // 5. Small delay for smooth transition (shows loading animation)
      await new Promise(resolve => setTimeout(resolve, 300));

      // 6. Navigate to Home - this will trigger fresh data fetches
      navigate('/');

    } finally {
      // 7. Hide loading overlay (after a brief moment for navigation to complete)
      setTimeout(() => {
        setIsSwitchingProfile(false);
      }, 200);
    }
  };

  const logoutProfile = () => {
    localStorage.removeItem('bootk_active_profile');
    localStorage.removeItem('bootk_active_profile_id');
    setActiveProfile(null);
    queryClient.removeQueries(); // Also clear cache on logout
    navigate('/');
  };

  return (
    <ActiveProfileContext.Provider value={{
      activeProfile,
      selectProfile,
      logoutProfile,
      profiles,
      isLoading,
      isSwitchingProfile, // Expose for loading overlay
    }}>
      {children}
    </ActiveProfileContext.Provider>
  );
}

export function useActiveProfile() {
  return useContext(ActiveProfileContext);
}